/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
/**
 * To download the file from the given sasUri
 *
 * @param sasUri string
 */
interface DownloadResult {
  status: "success" | "failed";
  error?: any;
  progress?: number;
}

interface LargeFileDownloadOptions {
  onProgress?: (progress: number) => void;
}

/**
 * SECURITY_NOTE: File downloads use secure SAS URIs with built-in authentication.
 * Large file downloads stream in chunks to prevent memory exhaustion.
 * Progress callbacks help prevent UI freezing during large downloads.
 */

/**
 * Original download method for regular files
 * Note: This method loads entire file into memory - suitable for files < 100MB
 * For large files (GB range), use downloadLargeFile instead
 * 
 * @param sasUri - SAS URI to download from
 * @param fileToDownload - Name of file to save
 */
export const downloadFile = async (
  sasUri: string,
  fileToDownload: string
): Promise<DownloadResult> => {
  try {
    const response = await fetch(sasUri, {
      method: "GET"
    });

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileToDownload;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { error: null, status: "success" };
  } catch (error: any) {
    return { error: error?.message || "Failed to download file", status: "failed" };
  }
};

/**
 * Download large files using direct browser navigation
 * For very large files (50GB+) in iframe contexts where memory is limited
 * This bypasses JavaScript memory entirely - browser handles the download natively
 * 
 * @param sasUri - SAS URI to download from
 * @param fileToDownload - Name of file to save
 * @param options - Optional configuration (progress callback)
 * @returns Promise with download result
 * 
 * @example
 * // Basic usage for large files
 * await downloadLargeFile(sasUri, "50gb-file.tar.gz");
 * 
 * @example
 * // With callback (fires when download initiates)
 * await downloadLargeFile(sasUri, "largefile.bin", {
 *   onProgress: (progress) => console.log(`Download initiated`)
 * });
 * 
 * @note This method uses direct browser navigation to avoid JavaScript memory limitations.
 * Progress tracking not available - browser's native download manager shows progress.
 */
export const downloadLargeFile = async (
  sasUri: string,
  fileToDownload: string,
  options?: LargeFileDownloadOptions
): Promise<DownloadResult> => {
  try {
    // Use direct browser navigation - lets browser's native download manager handle it
    // This completely bypasses JavaScript memory, preventing allocation errors
    
    const link = document.createElement("a");
    link.href = sasUri;
    link.download = fileToDownload;
    link.style.display = "none";
    link.rel = "noopener noreferrer";
    
    document.body.appendChild(link);
    link.click();
    
    // Cleanup after a short delay
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
    }, 100);

    // Indicate download has been initiated
    if (options?.onProgress) {
      options.onProgress(100);
    }

    return { error: null, status: "success", progress: 100 };
  } catch (error: any) {
    return { 
      error: error?.message || "Failed to download large file", 
      status: "failed" 
    };
  }
};
